// RecordPeopleControl — the ONE "whose record is this" edit control
// (0232 for service records; 0252 extends it to Opportunities and
// Processing).
//
// Presentational core extracted from ServiceRecordPeopleControl so the
// same chips/behaviors render on every record page — the adapter prop
// carries the four endpoint calls, so a service record, an opportunity,
// and an unlinked Processing record all get identical UX against their
// own routes. ServiceRecordPeopleControl remains as a thin wrapper, so
// its existing mount sites are untouched.
//
// New with 0252: a system-attributed person ('defaulted'/'backfill'/
// 'import') shows an "unconfirmed" hint and a one-click "Confirm" —
// the human judgment the provenance was waiting for, and what clears
// the cleanup confirm-lane without leaving the record.

import { useEffect, useState } from "react";
import { Mail } from "lucide-react";

import {
  api,
  ApiError,
  type CoHouseholdRecordKind,
  type DuplicatePrecheckMatch,
  type HouseholdContact,
  type RecordCoHousehold,
  type ServiceRecordPerson,
} from "../../api";
import { Button } from "../../ui";
import { useToast } from "../Toast";
import { memberDisplayName } from "../household-add-record/PersonSelect";
import { useEmailComposer } from "../../contexts/EmailComposerContext";
import { HouseholdPickerField } from "../HouseholdPickerField";
import { useHouseholdPicker } from "../use-household-picker";

export interface RecordPeopleAdapter {
  add(input: { contact_id: string }): Promise<unknown>;
  remove(contactId: string): Promise<unknown>;
  setPrimary(contactId: string): Promise<unknown>;
  confirm(contactId: string): Promise<unknown>;
}

/** Adapter for a service record's people (the 0232 endpoints). */
export function serviceRecordPeopleAdapter(
  serviceRecordId: string,
): RecordPeopleAdapter {
  return {
    add: (input) => api.addServiceRecordPerson(serviceRecordId, input),
    remove: (contactId) =>
      api.removeServiceRecordPerson(serviceRecordId, contactId),
    setPrimary: (contactId) =>
      api.setServiceRecordPrimaryPerson(serviceRecordId, contactId),
    confirm: (contactId) =>
      api.confirmServiceRecordPerson(serviceRecordId, contactId),
  };
}

/** Adapter for an opportunity's / unlinked transaction's own people
 *  (the 0252 endpoints). */
export function workflowRecordPeopleAdapter(
  kind: "opportunity" | "transaction",
  recordId: string,
): RecordPeopleAdapter {
  return {
    add: (input) => api.addWorkflowRecordPerson(kind, recordId, input),
    remove: (contactId) =>
      api.removeWorkflowRecordPerson(kind, recordId, contactId),
    setPrimary: (contactId) =>
      api.setWorkflowRecordPrimaryPerson(kind, recordId, contactId),
    confirm: (contactId) =>
      api.confirmWorkflowRecordPerson(kind, recordId, contactId),
  };
}

export function RecordPeopleControl({
  adapter,
  householdId,
  noun,
  people,
  onSaved,
  emptyStateText,
  coHouseholds,
}: {
  adapter: RecordPeopleAdapter;
  householdId: string;
  /** Per-company role word — "Insured", "Borrower", "Account holder", … */
  noun: string;
  people: ServiceRecordPerson[];
  onSaved: () => void;
  /** Copy for the zero-people state (differs by surface). */
  emptyStateText?: string;
  /**
   * The record that holds this control's people, when its company lets a
   * purchase include ANOTHER household (Realty + Mortgage, 2026-09-23 —
   * adult children buying with their parents). Omit it and the control
   * behaves exactly as before: household members only. The server
   * enforces the company rule independently.
   */
  coHouseholds?: { kind: CoHouseholdRecordKind; recordId: string };
}) {
  const toast = useToast();
  const composer = useEmailComposer();
  const [adding, setAdding] = useState(false);
  // "Replace X with…" — only offered when exactly one person is on the
  // record, because that is the one case where Remove is (rightly) hidden.
  const [replacing, setReplacing] = useState(false);
  const [members, setMembers] = useState<HouseholdContact[] | null>(null);
  const [busy, setBusy] = useState(false);
  // Other households on this purchase. null = not loaded (or not offered).
  const [linked, setLinked] = useState<RecordCoHousehold[] | null>(null);
  const [linkingHousehold, setLinkingHousehold] = useState(false);
  const picker = useHouseholdPicker();

  const coKind = coHouseholds?.kind;
  const coRecordId = coHouseholds?.recordId;
  useEffect(() => {
    if (!coKind || !coRecordId) {
      setLinked(null);
      return;
    }
    let cancelled = false;
    api
      .listRecordCoHouseholds(coKind, coRecordId)
      .then((r) => {
        if (!cancelled) setLinked(r.households);
      })
      .catch(() => {
        // A failed read leaves the record exactly as usable as before;
        // only the extra households are missing from the picker.
        if (!cancelled) setLinked([]);
      });
    return () => {
      cancelled = true;
    };
  }, [coKind, coRecordId]);

  async function linkPickedHousehold() {
    if (!coHouseholds) return;
    setBusy(true);
    try {
      const resolved = await picker.resolveHouseholdId();
      if (!resolved) return;
      const res = await api.addRecordCoHousehold(
        coHouseholds.kind,
        coHouseholds.recordId,
        resolved.householdId,
      );
      setLinked(res.households);
      const added = res.households.find((h) => h.household_id === resolved.householdId);
      toast.success(`Added ${added?.display_name ?? "the household"} to this purchase — now pick who signs`);
      picker.reset();
      setLinkingHousehold(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === "duplicate_contact") {
        // The debounced precheck missed a likely match and the server
        // caught it on create — show the same match panel the create
        // modals do, and stop before linking anything.
        const data = err.data as { matches?: DuplicatePrecheckMatch[] } | null;
        picker.reportServerDuplicate(data?.matches ?? []);
      } else {
        toast.error(err instanceof ApiError ? err.message : "Couldn't add that household");
      }
    } finally {
      setBusy(false);
    }
  }

  async function unlinkHousehold(h: RecordCoHousehold) {
    if (!coHouseholds) return;
    setBusy(true);
    try {
      const res = await api.removeRecordCoHousehold(
        coHouseholds.kind,
        coHouseholds.recordId,
        h.household_id,
      );
      setLinked(res.households);
      toast.success(`${h.display_name} is no longer on this purchase`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove that household");
    } finally {
      setBusy(false);
    }
  }

  async function openAdd() {
    setAdding(true);
    await loadMembers();
  }

  async function loadMembers() {
    if (members === null) {
      try {
        const detail = await api.getHousehold(householdId);
        setMembers(detail.contacts);
      } catch {
        setMembers([]);
        toast.error("Couldn't load the household's members");
      }
    }
  }

  async function openReplace() {
    setReplacing(true);
    await loadMembers();
  }

  // Replace the record's ONLY person with someone else. The OS never lets
  // a record sit with nobody on it (the server refuses to remove the last
  // person), so this is add first, then remove: the order that can never
  // leave the record empty. If the remove fails after the add worked, both
  // people stay on the record and the message says so, rather than
  // pretending it was an all-or-nothing swap. Removing the primary person
  // promotes the other one on the server, so the new person ends up
  // primary. Each step is an ordinary versioned, audited, scope-checked
  // call; nothing here has its own write path.
  async function replaceWith(oldPerson: ServiceRecordPerson, newId: string, newName: string) {
    setBusy(true);
    try {
      await adapter.add({ contact_id: newId });
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't update the people on this record",
      );
      setBusy(false);
      return;
    }
    try {
      await adapter.remove(oldPerson.contact_id);
      toast.success(`${newName} is now on this record instead of ${oldPerson.display_name}`);
    } catch {
      toast.error(
        `${newName} was added, but ${oldPerson.display_name} could not be removed. Both are on the record; use Remove next to ${oldPerson.display_name}.`,
      );
    }
    setReplacing(false);
    setBusy(false);
    onSaved();
  }

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      onSaved();
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : "Couldn't update the people on this record",
      );
    } finally {
      setBusy(false);
    }
  }

  const onRecord = new Set(people.map((p) => p.contact_id));
  const addable = (members ?? []).filter((m) => !onRecord.has(m.id));
  // People from the other households on this purchase, labeled with
  // their household so nobody mistakes a parent for a household member.
  const linkedAddable = (linked ?? []).flatMap((h) =>
    h.members
      .filter((m) => !onRecord.has(m.contact_id))
      .map((m) => ({ ...m, household_name: h.display_name })),
  );
  // Which other household a named person comes from, for their chip.
  const linkedHouseholdOf = new Map<string, string>();
  for (const h of linked ?? []) {
    for (const m of h.members) linkedHouseholdOf.set(m.contact_id, h.display_name);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {people.length === 0 ? (
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          {emptyStateText ??
            `No one is on this record yet — it predates person tracking. Add the ${noun.toLowerCase()} so the household reads whose record this is.`}
        </p>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {people.map((p) => (
            <span
              key={p.contact_id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                border: "1px solid var(--mid-gray, #e0e6ec)",
                borderRadius: 999,
                padding: "3px 10px",
              }}
            >
              <span>
                {p.display_name}
                {p.is_primary && people.length > 1 ? " · primary" : ""}
              </span>
              {linkedHouseholdOf.has(p.contact_id) && (
                <span className="muted" style={{ fontSize: 10 }}>
                  {linkedHouseholdOf.get(p.contact_id)}
                </span>
              )}
              {p.email && (
                // Write to just this person (Nate, 2026-08-12) without
                // opening the household. Same Graph 1:1 composer every
                // email button on the OS uses.
                <button
                  type="button"
                  className="hh-co-advisor-row-edit-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    fontSize: 10,
                  }}
                  title={`Email ${p.display_name} (${p.email})`}
                  onClick={() =>
                    composer.openWith({
                      householdId,
                      prefillTo: [p.email!],
                    })
                  }
                >
                  <Mail size={11} strokeWidth={1.5} aria-hidden />
                </button>
              )}
              {p.source !== "user" && (
                // System-attributed, no human has confirmed it. Plain
                // words + a one-click confirm (0252) — this is what the
                // cleanup confirm-lane routes people to.
                <>
                  <span
                    className="muted"
                    title="The system attributed this person automatically — nobody has confirmed it yet."
                    style={{ fontSize: 10 }}
                  >
                    unconfirmed
                  </span>
                  <button
                    type="button"
                    className="hh-co-advisor-row-edit-btn"
                    style={{ fontSize: 10 }}
                    disabled={busy}
                    title={`Confirm ${p.display_name} is the right ${noun.toLowerCase()} on this record`}
                    onClick={() =>
                      void run(`Confirmed ${p.display_name} on this record`, () =>
                        adapter.confirm(p.contact_id),
                      )
                    }
                  >
                    Confirm
                  </button>
                </>
              )}
              {!p.is_primary && (
                <button
                  type="button"
                  className="hh-co-advisor-row-edit-btn"
                  style={{ fontSize: 10 }}
                  disabled={busy}
                  title={`Make ${p.display_name} the primary ${noun.toLowerCase()}`}
                  onClick={() =>
                    void run(
                      `${p.display_name} is now the primary ${noun.toLowerCase()}`,
                      () => adapter.setPrimary(p.contact_id),
                    )
                  }
                >
                  Make primary
                </button>
              )}
              {people.length > 1 && (
                <button
                  type="button"
                  className="hh-co-advisor-row-edit-btn"
                  style={{ fontSize: 10 }}
                  disabled={busy}
                  title={`Remove ${p.display_name} from this record`}
                  onClick={() =>
                    void run(`Removed ${p.display_name} from this record`, () =>
                      adapter.remove(p.contact_id),
                    )
                  }
                >
                  Remove
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {linked && linked.length > 0 && (
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", fontSize: 12 }}
        >
          <span className="muted">Also on this purchase:</span>
          {linked.map((h) => (
            <span
              key={h.household_id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                border: "1px dashed var(--mid-gray, #e0e6ec)",
                borderRadius: 999,
                padding: "3px 10px",
              }}
            >
              <a href={`/households/${encodeURIComponent(h.household_id)}`}>{h.display_name}</a>
              <button
                type="button"
                className="hh-co-advisor-row-edit-btn"
                style={{ fontSize: 10 }}
                disabled={busy}
                title={`Take ${h.display_name} off this purchase. Its people have to come off the record first.`}
                onClick={() => void unlinkHousehold(h)}
              >
                Remove
              </button>
            </span>
          ))}
        </div>
      )}

      {people.length === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <p className="muted" style={{ fontSize: 12, margin: 0 }}>
            {people[0]!.display_name} is the only person on this record, and a record always
            keeps at least one. To swap them, pick the right person here; to add someone
            alongside, use "+ Add person" and Remove appears once there are two.
          </p>
          {!replacing ? (
            <div>
              <Button
                variant="sw-default"
                onClick={() => void openReplace()}
                disabled={busy}
                style={{ fontSize: 12, padding: "3px 10px" }}
              >
                Replace {people[0]!.display_name} with…
              </Button>
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              {members === null ? (
                <span className="muted" style={{ fontSize: 12 }}>
                  Loading members…
                </span>
              ) : addable.length === 0 && linkedAddable.length === 0 ? (
                <span className="muted" style={{ fontSize: 12 }}>
                  No one else in this household to put on the record. Add them to the household first.
                </span>
              ) : (
                <>
                  {addable.map((m) => (
                    <Button
                      key={m.id}
                      variant="sw-default"
                      disabled={busy}
                      style={{ fontSize: 12, padding: "3px 10px" }}
                      onClick={() => void replaceWith(people[0]!, m.id, memberDisplayName(m))}
                    >
                      {memberDisplayName(m)}
                    </Button>
                  ))}
                  {linkedAddable.map((m) => (
                    <Button
                      key={m.contact_id}
                      variant="sw-default"
                      disabled={busy}
                      style={{ fontSize: 12, padding: "3px 10px" }}
                      title={`${m.display_name} is in ${m.household_name}`}
                      onClick={() => void replaceWith(people[0]!, m.contact_id, m.display_name)}
                    >
                      {m.display_name} · {m.household_name}
                    </Button>
                  ))}
                </>
              )}
              <Button
                variant="ghost"
                onClick={() => setReplacing(false)}
                disabled={busy}
                style={{ fontSize: 12, padding: "3px 10px" }}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}

      {!adding ? (
        <div>
          <Button
            variant="sw-default"
            onClick={() => void openAdd()}
            disabled={busy}
            style={{ fontSize: 12, padding: "3px 10px" }}
          >
            + Add person
          </Button>
        </div>
      ) : (
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}
        >
          {members === null ? (
            <span className="muted" style={{ fontSize: 12 }}>
              Loading members…
            </span>
          ) : addable.length === 0 && linkedAddable.length === 0 ? (
            <span className="muted" style={{ fontSize: 12 }}>
              {linked && linked.length > 0
                ? "Everyone in these households is already on the record."
                : "Everyone in this household is already on the record."}
            </span>
          ) : (
            addable.map((m) => (
              <Button
                key={m.id}
                variant="sw-default"
                disabled={busy}
                style={{ fontSize: 12, padding: "3px 10px" }}
                onClick={() =>
                  void run(`Added ${memberDisplayName(m)} to this record`, () =>
                    adapter.add({ contact_id: m.id }),
                  )
                }
              >
                + {memberDisplayName(m)}
              </Button>
            ))
          )}
          {linkedAddable.map((m) => (
            <Button
              key={m.contact_id}
              variant="sw-default"
              disabled={busy}
              style={{ fontSize: 12, padding: "3px 10px" }}
              title={`${m.display_name} is in ${m.household_name}`}
              onClick={() =>
                void run(`Added ${m.display_name} to this record`, () =>
                  adapter.add({ contact_id: m.contact_id }),
                )
              }
            >
              + {m.display_name} · {m.household_name}
            </Button>
          ))}
          {coHouseholds && !linkingHousehold && (
            <Button
              variant="ghost"
              disabled={busy}
              style={{ fontSize: 12, padding: "3px 10px" }}
              title="For a purchase made together with people from another household, like parents buying with their kids"
              onClick={() => setLinkingHousehold(true)}
            >
              + Add another household
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => {
              setAdding(false);
              setLinkingHousehold(false);
              picker.reset();
            }}
            disabled={busy}
            style={{ fontSize: 12, padding: "3px 10px" }}
          >
            Done
          </Button>
        </div>
      )}

      {adding && linkingHousehold && coHouseholds && (
        <div
          style={{
            border: "1px solid var(--mid-gray, #e0e6ec)",
            borderRadius: 6,
            padding: "4px 10px 10px",
            maxWidth: 480,
          }}
        >
          <p className="muted" style={{ fontSize: 12, margin: "8px 0 0" }}>
            Buying together with another household? Find them here. This record stays with its
            own household; theirs is added alongside it, and then you pick who signs.
          </p>
          <HouseholdPickerField picker={picker} autoFocus />
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <Button
              variant="sw-default"
              disabled={busy || !picker.ready || picker.blockedByDuplicate}
              style={{ fontSize: 12, padding: "3px 10px" }}
              onClick={() => void linkPickedHousehold()}
            >
              Add this household to the purchase
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              style={{ fontSize: 12, padding: "3px 10px" }}
              onClick={() => {
                setLinkingHousehold(false);
                picker.reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
